'use client'

import { useState } from 'react'
import { Camera, Clock, Sparkles, ListChecks } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAuth } from '@/components/AuthProvider'
import { useDemoStatus } from '@/hooks/useDemoStatus'
import { useTranslations } from '@/hooks/useTranslations'
import { usePathname } from '@/hooks/usePathname'
import { isMarketingPath } from '@/lib/route-gates'
import { canShowPurchaseUi } from '@/lib/purchase-surface'
import { hasSeenDemoIntro, markDemoIntroSeen } from '@/lib/onboarding-state'

/**
 * One-off welcome dialog for a freshly approved demo account: what's included,
 * how long it runs, and what happens when it ends. Until now a new tester
 * landed straight on /detect with nothing but a banner — the only place any of
 * this was explained was the approval email.
 *
 * Shows once per user per device (see onboarding-state.ts), only for an
 * unblocked demo account, and never on top of the pending/rejected screens
 * (those replace the whole app, so the status check keeps us out of the way).
 *
 * The "what happens after" line differs on native: no price, no purchase
 * call-to-action. See canShowPurchaseUi().
 */
export function DemoIntroDialog() {
  const { user } = useAuth()
  const { demo } = useDemoStatus()
  const t = useTranslations('demoIntro')
  const pathname = usePathname()
  const [dismissed, setDismissed] = useState(false)

  // `dismissed` is checked before the storage read on purpose. The user id is
  // null on first paint and arrives once AuthProvider resolves, so a
  // mount-time snapshot of the flag would be taken against `undefined` and
  // then wrongly re-show the dialog for someone who had already dismissed it.
  if (dismissed) return null
  // AccountGate passes marketing/auth routes straight through, so this sits in
  // the tree on /login too. A logged-in demo user landing there would otherwise
  // get a welcome modal thrown over the login form.
  if (isMarketingPath(pathname)) return null
  if (!user?.id || !demo?.isDemo) return null
  if (demo.blocked) return null
  if (hasSeenDemoIntro(user.id)) return null

  const close = () => {
    markDemoIntroSeen(user.id)
    setDismissed(true)
  }

  const days = demo.daysLeft
  const limit = demo.analysesLimit

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-intro-title"
      onClick={close}
    >
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <CardContent className="pt-6 space-y-5">
          <div className="text-center space-y-2">
            <div className="flex justify-center">
              <div className="rounded-full bg-[#001c4d]/5 p-3">
                <Sparkles className="w-7 h-7 text-[#001c4d]" />
              </div>
            </div>
            <h2 id="demo-intro-title" className="text-xl font-semibold text-[#001c4d]">
              {t('title')}
            </h2>
            <p className="text-sm text-gray-600">{t('subtitle')}</p>
          </div>

          <ul className="space-y-3 text-sm">
            {limit != null && (
              <li className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#001c4d] flex-none mt-0.5" />
                <span>{t('includedAnalyses', { limit })}</span>
              </li>
            )}
            {days != null && (
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#001c4d] flex-none mt-0.5" />
                <span>{t('includedDays', { days })}</span>
              </li>
            )}
            <li className="flex items-start gap-3">
              <Camera className="w-5 h-5 text-[#001c4d] flex-none mt-0.5" />
              <span>{t('howToStart')}</span>
            </li>
            <li className="flex items-start gap-3">
              <ListChecks className="w-5 h-5 text-[#001c4d] flex-none mt-0.5" />
              <span>{t('whatCounts')}</span>
            </li>
          </ul>

          <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-700">
            {canShowPurchaseUi() ? t('afterWeb') : t('afterNative')}
          </div>

          <Button onClick={close} className="w-full bg-[#001c4d] hover:bg-[#001c4d]/90">
            {t('cta')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
